import React, { FC, useEffect, useState } from 'react';
import { CSSObject, Theme, styled } from '@mui/material/styles';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import EmojiTransportationOutlinedIcon from '@mui/icons-material/EmojiTransportationOutlined';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { ToothOutline } from 'mdi-material-ui';
import DrawerMenu from './drawer-menu';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { DrawerMenuItem } from 'models/navigation';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import SvgIcon from '@mui/material/SvgIcon';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Organization } from 'models/organization';
import MuiDrawer from '@mui/material/Drawer';
import OutboxIcon from '@mui/icons-material/Outbox';

type DrawerMenuContainerProps = {
    isOpen: boolean;
    onNavigation: () => void;
};

const drawerWidth = 225;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 0,
    [theme.breakpoints.up('md')]: {
        width: 53,
    },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

const DrawerMenuContainer: FC<DrawerMenuContainerProps> = ({ isOpen, onNavigation }: DrawerMenuContainerProps) => {
    const { t: translate } = useTranslation(['navigation', 'client-directory']);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [transportDefaultUrl, setTransportDefaultUrl] = useState('/transport/transport-schedule');

    const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
    );

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    // DD-3687: the archive is opt-in per organization. Every *internal* user of
    // an activated organization sees it, admin or not — but no external tier
    // does: it is a lab-wide listing of the customer's whole order history.
    const isHistoricalOrdersEnabled =
        customerSettings.getCustomerSetting(
            currentOrganization.customerSettings,
            CONSTANTS.SETTINGS_NAMES.HISTORICAL_ORDERS,
        ) && !permissions.isExternalTechnician(currentUser?.role);

    useEffect(() => {
        const hasNshift = currentOrganization.transportSettings?.nShiftTransport?.isEnabled || false;
        const hasDenthubTransport = currentOrganization.transportSettings?.denthubTransport?.isEnabled || false;
        if (hasNshift && !hasDenthubTransport) {
            setTransportDefaultUrl('/transport/delivery-planner');
        }
    }, []);

    const clinicSvgIcon: FC = () => {
        return (
            <SvgIcon>
                <svg fill="rgba(0, 0, 0, 0.54)" version="1.1" x="0px" y="0px" viewBox="0 0 512 512" xmlSpace="preserve">
                    <g>
                        <path d="M349.8,162.3V68.5H162.3v93.8H68.5v281.3h375V162.3H349.8z M181,387.3h-75v-37.5h75V387.3z M181,312.3h-75v-37.5h75V312.3z    M181,237.3h-75v-37.5h75V237.3z M293.5,387.3h-75v-37.5h75V387.3z M293.5,312.3h-75v-37.5h75V312.3z M312.3,181h-37.5v37.5h-37.5   V181h-37.5v-37.5h37.5V106h37.5v37.5h37.5V181z M406,387.3h-75v-37.5h75V387.3z M406,312.3h-75v-37.5h75V312.3z M406,237.3h-75   v-37.5h75V237.3z" />
                    </g>
                </svg>
            </SvgIcon>
        );
    };

    // TODO: SitemapOutline currently not in mdi-material-ui package, replace this once it is there...
    const siteMapOutlineSvgIcon: FC = () => {
        return (
            <SvgIcon>
                <svg fill="rgba(0, 0, 0, 0.54)" version="1.1" x="0px" y="0px" viewBox="0 0 24 24" xmlSpace="preserve">
                    <g>
                        <path d="M21 16V13C21 11.89 20.11 11 19 11H13V8H15V2H9V8H11V11H5C3.89 11 3 11.89 3 13V16H1V22H7V16H5V13H11V16H9V22H15V16H13V13H19V16H17V22H23V16H21M11 4H13V6H11V4M5 20H3V18H5V20M13 20H11V18H13V20M21 20H19V18H21V20Z" />
                    </g>
                </svg>
            </SvgIcon>
        );
    };

    // TODO: Move to shared
    const isInvoicingEnabled = () => {
        return JSON.parse(
            customerSettings.getCustomerSetting(
                currentOrganization.customerSettings,
                CONSTANTS.SETTINGS_NAMES.INVOICING_MODULE,
            ) || null,
        );
    };
    // TODO: Move to shared
    const isActionBasedOrder = () => {
        return (
            CONSTANTS.SETTINGS_VALUES.ACTION_BASED ===
            customerSettings.getCustomerSetting(
                currentOrganization.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ORDER_VERSION,
            )
        );
    };
    const privateElasticDashboard = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRIVATE_ELASTIC_DASHBOARD,
    );
    const menuForDentists = [
        {
            Icon: AddCircleOutlinedIcon,
            name: translate('NEW_ORDER'),
            url: '/orders/create-action',
            isVisible: isActionBasedOrder() && !permissions.isCarrier(currentUser.role),
            isNewOrder: true,
        },
        {
            Icon: AddCircleOutlinedIcon,
            name: translate('NEW_ORDER'),
            url: 'orders/create-simple',
            isVisible: !isActionBasedOrder() && !permissions.isCarrier(currentUser.role),
            isNewOrder: true,
        },
        {
            Icon: DescriptionOutlinedIcon,
            name: translate('ORDERS'),
            url: '/orders/list',
            isVisible: currentUser?.permissions?.permissions?.GET_ORDERS && !permissions.isCarrier(currentUser.role),
        },
        {
            Icon: AssignmentIndOutlinedIcon,
            name: translate('PATIENTS'),
            url: '/patients/list/patients',
            isVisible: currentUser?.permissions?.permissions?.LIST_PATIENTS,
        },
        {
            Icon: MailOutlineIcon,
            name: translate('REFERRALS'),
            url: '/referrals',
            isVisible: false,
        },
        {
            Icon: Inventory2OutlinedIcon,
            name: translate('HISTORICAL_ORDERS'),
            url: '/historical-orders',
            isVisible: isHistoricalOrdersEnabled,
        },

        {
            Icon: AccountBalanceWalletOutlinedIcon,
            name: translate('INVOICING'),
            url: `/invoices/list`,
            isVisible: permissions.isDentist(currentUser?.type),
        },
        {
            Icon: AssessmentOutlinedIcon,
            name: translate('REPORTS'),
            url: '/reports',
            isVisible:
                currentUser?.permissions?.permissions?.REPORTS &&
                !permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization),
        },
        {
            Icon: InsightsOutlinedIcon,
            name: translate('ANALYTICS'),
            url: '/analytics/private-space',
            isVisible: !!privateElasticDashboard && permissions.isDentistAdmin(currentUser?.role),
        },
        {
            Icon: PeopleOutlineOutlinedIcon,
            name: translate('USERS'),
            url: '/dentists',
            isVisible:
                (permissions.isDentistAdmin(currentUser?.role) && currentUser?.permissions?.permissions?.GET_USERS) ||
                permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization),
        },
        {
            Icon: clinicSvgIcon,
            name: translate('CLINICS'),
            url: '/clinics',
            isVisible: permissions.isDentistAdmin(currentUser?.role),
        },
        {
            Icon: ToothOutline,
            name: translate('LABS'),
            // DD-3696: opens My labs; "Find labs" is the page's second tab.
            url: '/simple-connected-labs',
            // DD-3702: clinic managers too — they answer labs' requests on the Requests tab.
            isVisible:
                (permissions.isDentistAdmin(currentUser?.role) || permissions.isClinicAdmin(currentUser?.role)) &&
                !isActionBasedOrder() &&
                !permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization),
        },
        {
            Icon: ToothOutline,
            name: translate('LABS'),
            url: '/action-based/manage-labs',
            isVisible:
                permissions.isDentistAdmin(currentUser?.role) &&
                isActionBasedOrder() &&
                !permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization),
        },
        {
            Icon: AccountTreeOutlinedIcon,
            name: translate('ACTIONS'),
            url: '/action-based/manage-actions',
            isVisible: currentUser?.permissions?.permissions?.EDIT_ACTIONS && isActionBasedOrder(),
        },
        {
            // DD-3697: the backend limits price requests to the chain's admins.
            Icon: RequestQuoteOutlinedIcon,
            name: translate('PRICE_REQUESTS'),
            url: '/price-requests',
            isVisible:
                permissions.isDentistAdmin(currentUser?.role) &&
                isActionBasedOrder() &&
                !permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization),
        },
    ];
    const menuForLabs = [
        {
            Icon: AddCircleOutlinedIcon,
            name: translate('NEW_ORDER'),
            url: '/orders/create-lab',
            isVisible:
                !permissions.isExternalTechnician(currentUser?.role) && !permissions.isCarrier(currentUser?.role),
            isNewOrder: true,
        },
        {
            Icon: DescriptionOutlinedIcon,
            name: translate('ORDERS'),
            url: '/orders/list',
            isVisible: currentUser?.permissions?.permissions?.GET_ORDERS,
        },
        {
            // DD-3800: Workflow was renamed Production; the old page lives under its Workflow tab.
            Icon: siteMapOutlineSvgIcon,
            name: translate('PRODUCTION'),
            url: '/production',
            isVisible: isLabWorkflowEnabled,
        },
        {
            Icon: CalendarMonthOutlinedIcon,
            name: translate('PRODUCTION_PLANNING'),
            url: '/production-dashboard',
            isVisible: isProductionPlanningEnabled && permissions.isInternalTechnician(currentUser?.role),
        },
        {
            Icon: LocalShippingOutlinedIcon,
            name: translate('TRANSPORT'),
            url: transportDefaultUrl,
            isVisible: currentOrganization.isTransportEnable && !permissions.isExternalTechnician(currentUser?.role),
        },
        {
            Icon: AccountBalanceWalletOutlinedIcon,
            name: translate('INVOICING'),
            url: `/invoices/invoices`,
            isVisible: isInvoicingEnabled() && !permissions.isExternalTechnician(currentUser?.role),
        },
        {
            Icon: OutboxIcon,
            name: translate('client-directory:SUPPLIERS'),
            url: `/suppliers/supplier-orders`,
            isVisible: permissions.isInternalTechnician(currentUser?.role),
        },
        {
            Icon: InsightsOutlinedIcon,
            name: translate('ANALYTICS'),
            url: '/analytics/order-overview',
            isVisible: permissions.isTechnicianAdmin(currentUser?.role),
        },
        {
            Icon: AssignmentIndOutlinedIcon,
            name: translate('PATIENTS'),
            url: '/patients/list/patients',
            isVisible: currentUser?.permissions?.permissions?.LIST_PATIENTS,
        },
        {
            Icon: MenuBookIcon,
            name: translate('CLIENT_DIRECTORY'),
            url: `/client-directory/clients`,
            isVisible: permissions.isTechnicianAdmin(currentUser?.role),
        },
        {
            Icon: AssessmentOutlinedIcon,
            name: translate('REPORTS'),
            url: '/reports',
            isVisible:
                currentUser?.permissions?.permissions?.REPORTS &&
                !permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization),
        },
        {
            Icon: PeopleOutlineOutlinedIcon,
            name: translate('USERS'),
            url: '/technicians',
            isVisible: permissions.isTechnicianAdmin(currentUser?.role),
        },
        {
            Icon: Inventory2OutlinedIcon,
            name: translate('HISTORICAL_ORDERS'),
            url: '/historical-orders',
            isVisible: isHistoricalOrdersEnabled,
        },
        {
            Icon: SettingsOutlinedIcon,
            name: translate('SETTINGS'),
            url: '/lab-settings/general-information',
            isVisible: permissions.isTechnicianAdmin(currentUser?.role),
        },
        {
            Icon: EmojiTransportationOutlinedIcon,
            name: translate('ADMIN_TRANSPORT'),
            url: '/admin-transport/transport-orders',
            isVisible: permissions.isCarrier(currentUser?.role),
            isAnchor: false,
        },
    ];

    return (
        <Drawer variant="permanent" open={isOpen}>
            {permissions.isDentist(currentUser?.type) ? (
                <DrawerMenu onNavigation={onNavigation} menu={menuForDentists as DrawerMenuItem[]} />
            ) : undefined}
            {permissions.isLabTechnician(currentUser?.type) ? (
                <DrawerMenu onNavigation={onNavigation} menu={menuForLabs as DrawerMenuItem[]} />
            ) : undefined}
        </Drawer>
    );
};

export default DrawerMenuContainer;
